import React from "react";
import {
  AbsoluteFill,
  interpolate,
  Sequence,
  spring,
  useCurrentFrame,
  useVideoConfig,
} from "remotion";
import { SceneTint } from "../components/SceneChrome";
import StackChip from "../components/StackChip";
import { projectById } from "../data/resume";
import { SCENES, SENIOR_BEATS } from "../data/scenes";

const beatLocal = (beat: { from: number; duration: number }) => ({
  from: beat.from - SCENES.seniorEra.from,
  duration: beat.duration,
});

// Fades a beat in/out at its edges so beats hand over cleanly.
const BeatFade: React.FC<{
  duration: number;
  children: React.ReactNode;
}> = ({ duration, children }) => {
  const frame = useCurrentFrame();
  return (
    <AbsoluteFill
      style={{
        opacity: interpolate(
          frame,
          [0, 5, duration - 5, duration],
          [0, 1, 1, 0],
          { extrapolateLeft: "clamp", extrapolateRight: "clamp" }
        ),
      }}
    >
      {children}
    </AbsoluteFill>
  );
};

const BeatTitle: React.FC<{
  title: string;
  subtitle: string;
  color: string;
}> = ({ title, subtitle, color }) => {
  const frame = useCurrentFrame();
  return (
    <div
      style={{
        position: "absolute",
        top: 64,
        left: 0,
        right: 0,
        textAlign: "center",
        opacity: interpolate(frame, [0, 10], [0, 1], {
          extrapolateLeft: "clamp",
          extrapolateRight: "clamp",
        }),
      }}
    >
      <div
        style={{
          fontFamily: "Montserrat, sans-serif",
          fontWeight: 600,
          fontSize: 52,
          color: "white",
        }}
      >
        {title}
      </div>
      <div
        style={{
          fontFamily: "'JetBrains Mono', monospace",
          fontSize: 25,
          color,
          marginTop: 6,
        }}
      >
        {subtitle}
      </div>
    </div>
  );
};

// ── Chapter intro: "SENIOR" lands with a heavy spring ────────────────────────
const IntroBeat: React.FC = () => {
  const frame = useCurrentFrame();
  const { fps } = useVideoConfig();
  const land = spring({
    frame: frame - 4,
    fps,
    config: { damping: 11, stiffness: 120, mass: 1.4 },
  });
  return (
    <AbsoluteFill style={{ alignItems: "center", justifyContent: "center" }}>
      <SceneTint color="#8B5CF6" enterFrame={0} intensity={0.8} />
      <div
        style={{
          fontFamily: "'JetBrains Mono', monospace",
          fontSize: 30,
          color: "#b3b3b3",
          letterSpacing: 8,
        }}
      >
        CHAPTER 4 · PALO IT · 2024–2026
      </div>
      <div
        style={{
          fontFamily: "Montserrat, sans-serif",
          fontWeight: 700,
          fontSize: 190,
          color: "white",
          letterSpacing: 14,
          scale: String(3 - land * 2),
          opacity: land,
          textShadow: "0 0 90px #8B5CF688",
        }}
      >
        SENIOR AI
      </div>
      <div
        style={{
          fontFamily: "Montserrat, sans-serif",
          fontSize: 52,
          color: "#8B5CF6",
          opacity: interpolate(frame, [14, 22], [0, 1], {
            extrapolateLeft: "clamp",
            extrapolateRight: "clamp",
          }),
        }}
      >
        Consultant Era
      </div>
    </AbsoluteFill>
  );
};

// ── Beat 1: Vista hero panel with 4 sub-beats ────────────────────────────────
const SPAGHETTI = [
  "M 200 120 C 500 420, 320 80, 760 300 S 520 480, 900 200",
  "M 160 380 C 460 120, 700 520, 980 160 S 700 420, 1080 380",
  "M 260 260 C 80 480, 820 60, 620 440 S 980 300, 820 100",
  "M 120 200 C 620 260, 240 520, 1040 260 S 640 120, 960 480",
  "M 340 60 C 140 380, 900 440, 700 120 S 420 520, 1100 200",
];

const VistaBeat: React.FC = () => {
  const frame = useCurrentFrame();
  const { fps } = useVideoConfig();
  const vista = projectById("vista");
  const sub = (start: number, end: number) =>
    interpolate(frame, [start, start + 5, end - 5, end], [0, 1, 1, 0], {
      extrapolateLeft: "clamp",
      extrapolateRight: "clamp",
    });
  const DOCK_CHIPS = ["GitHub", "Azure", "Cursor", "Copilot", "OpenRouter"];
  const GRID_COLS = 10;
  const GRID_ROWS = 5;

  return (
    <AbsoluteFill style={{ alignItems: "center", justifyContent: "center" }}>
      <SceneTint color={vista.color} enterFrame={0} />
      <BeatTitle
        title="Vista — Agentic Orchestrator"
        subtitle="Palo IT · Senior Consultant"
        color={vista.color}
      />
      <div
        style={{
          position: "relative",
          width: 1500,
          height: 720,
          marginTop: 90,
          borderRadius: 28,
          border: `3px solid ${vista.color}`,
          backgroundColor: "rgba(24,24,24,0.92)",
          boxShadow: `0 0 80px ${vista.color}33`,
          overflow: "hidden",
        }}
      >
        {/* Sub-beat 1 — spaghetti untangles into workflows / rules / skills */}
        <AbsoluteFill
          style={{
            alignItems: "center",
            justifyContent: "center",
            opacity: sub(0, 18),
          }}
        >
          <svg width={1200} height={540} viewBox="0 0 1200 540">
            {SPAGHETTI.map((d, i) => (
              <path
                key={i}
                d={d}
                fill="none"
                stroke="#777"
                strokeWidth={4}
                opacity={interpolate(frame, [6, 14], [0.8, 0], {
                  extrapolateLeft: "clamp",
                  extrapolateRight: "clamp",
                })}
              />
            ))}
          </svg>
          <div
            style={{
              position: "absolute",
              display: "flex",
              flexDirection: "row",
              gap: 60,
            }}
          >
            {["workflows", "rules", "skills"].map((label, i) => (
              <div
                key={label}
                style={{
                  width: 300,
                  height: 130,
                  borderRadius: 20,
                  border: `3px solid ${vista.color}`,
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  fontFamily: "'JetBrains Mono', monospace",
                  fontSize: 34,
                  color: "white",
                  backgroundColor: "#8B5CF622",
                  scale: String(
                    spring({
                      frame: frame - (7 + i * 2),
                      fps,
                      config: { damping: 13, stiffness: 160 },
                    })
                  ),
                }}
              >
                {label}
              </div>
            ))}
          </div>
          <div
            style={{
              position: "absolute",
              bottom: 40,
              fontFamily: "Montserrat, sans-serif",
              fontSize: 27,
              color: "#d6d6d6",
            }}
          >
            Complex business logic → agentic workflows, rules & skills
          </div>
        </AbsoluteFill>

        {/* Sub-beat 2 — orchestrator hub, model nodes orbit */}
        <AbsoluteFill
          style={{
            alignItems: "center",
            justifyContent: "center",
            opacity: sub(16, 34),
          }}
        >
          <div
            style={{
              width: 260,
              height: 260,
              borderRadius: 130,
              border: `4px solid ${vista.color}`,
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              fontFamily: "'JetBrains Mono', monospace",
              fontSize: 28,
              color: "white",
              backgroundColor: "#8B5CF62d",
              boxShadow: `0 0 ${50 + Math.sin(frame / 4) * 20}px ${
                vista.color
              }66`,
            }}
          >
            ORCHESTRATOR
          </div>
          {[0, 1, 2, 3, 4].map((i) => {
            const angle = (i / 5) * Math.PI * 2 + frame * 0.045;
            return (
              <div
                key={i}
                style={{
                  position: "absolute",
                  left: "50%",
                  top: "50%",
                  translate: `${Math.cos(angle) * 300 - 30}px ${
                    Math.sin(angle) * 210 - 30
                  }px`,
                  width: 60,
                  height: 60,
                  borderRadius: 30,
                  backgroundColor: "#241d38",
                  border: `2px solid ${vista.color}`,
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  fontFamily: "'JetBrains Mono', monospace",
                  fontSize: 20,
                  color: "#c4b5fd",
                }}
              >
                LLM
              </div>
            );
          })}
          <div
            style={{
              position: "absolute",
              bottom: 40,
              fontFamily: "Montserrat, sans-serif",
              fontSize: 27,
              color: "#d6d6d6",
            }}
          >
            One orchestrator, complex workflows, any model
          </div>
        </AbsoluteFill>

        {/* Sub-beat 3 — integrations dock; ticket in → merged PR out */}
        <AbsoluteFill
          style={{
            alignItems: "center",
            justifyContent: "center",
            opacity: sub(32, 50),
          }}
        >
          <div style={{ display: "flex", flexDirection: "row", gap: 18 }}>
            {DOCK_CHIPS.map((chip, i) => (
              <div
                key={chip}
                style={{
                  padding: "14px 26px",
                  borderRadius: 18,
                  border: `3px solid ${vista.color}`,
                  fontFamily: "'JetBrains Mono', monospace",
                  fontSize: 28,
                  color: "white",
                  backgroundColor: "#241d38",
                  translate: `0px ${
                    (1 -
                      spring({
                        frame: frame - (34 + i * 2),
                        fps,
                        config: { damping: 14, stiffness: 180 },
                      })) *
                    140
                  }px`,
                }}
              >
                {chip}
              </div>
            ))}
          </div>
          <div
            style={{
              display: "flex",
              flexDirection: "row",
              alignItems: "center",
              gap: 30,
              marginTop: 70,
              fontFamily: "'JetBrains Mono', monospace",
              fontSize: 30,
            }}
          >
            <div
              style={{
                padding: "12px 24px",
                borderRadius: 14,
                backgroundColor: "#332b1a",
                border: "2px solid #E8B923",
                color: "#E8B923",
                translate: `${interpolate(frame, [36, 44], [-500, 0], {
                  extrapolateLeft: "clamp",
                  extrapolateRight: "clamp",
                })}px 0px`,
              }}
            >
              ticket #4821
            </div>
            <div style={{ color: "#777" }}>→ agents →</div>
            <div
              style={{
                padding: "12px 24px",
                borderRadius: 14,
                backgroundColor: "#16311f",
                border: "2px solid #2E9E5B",
                color: "#2E9E5B",
                opacity: interpolate(frame, [44, 48], [0, 1], {
                  extrapolateLeft: "clamp",
                  extrapolateRight: "clamp",
                }),
              }}
            >
              ✓ PR merged
            </div>
          </div>
          <div
            style={{
              position: "absolute",
              bottom: 40,
              fontFamily: "Montserrat, sans-serif",
              fontSize: 27,
              color: "#d6d6d6",
            }}
          >
            Tickets picked up & completed autonomously — high accuracy, high
            quality
          </div>
        </AbsoluteFill>

        {/* Sub-beat 4 — WinForms dissolves cell-by-cell into modern Angular */}
        <AbsoluteFill
          style={{
            alignItems: "center",
            justifyContent: "center",
            opacity: sub(48, 63),
          }}
        >
          <div
            style={{
              display: "grid",
              gridTemplateColumns: `repeat(${GRID_COLS}, 74px)`,
              gap: 6,
            }}
          >
            {Array.from({ length: GRID_COLS * GRID_ROWS }).map((_, i) => {
              const col = i % GRID_COLS;
              const row = Math.floor(i / GRID_COLS);
              const modern = interpolate(
                frame,
                [50 + (col + row) * 0.8, 54 + (col + row) * 0.8],
                [0, 1],
                { extrapolateLeft: "clamp", extrapolateRight: "clamp" }
              );
              return (
                <div
                  key={i}
                  style={{
                    width: 74,
                    height: 58,
                    borderRadius: modern > 0.5 ? 10 : 2,
                    backgroundColor: modern > 0.5 ? "#2d2447" : "#4a4a4a",
                    border:
                      modern > 0.5
                        ? `2px solid ${vista.color}`
                        : "2px solid #6e6e6e",
                    opacity: 0.5 + modern * 0.5,
                    scale: String(0.9 + modern * 0.1),
                  }}
                />
              );
            })}
          </div>
          <div
            style={{
              marginTop: 44,
              fontFamily: "'JetBrains Mono', monospace",
              fontSize: 54,
              color: "white",
            }}
          >
            {Math.round(
              interpolate(frame, [50, 62], [1980, 2026], {
                extrapolateLeft: "clamp",
                extrapolateRight: "clamp",
              })
            )}
          </div>
          <div
            style={{
              fontFamily: "Montserrat, sans-serif",
              fontSize: 27,
              color: "#d6d6d6",
              marginTop: 8,
            }}
          >
            WinForms → Angular/C# — implementation plans actioned through the
            orchestrator
          </div>
        </AbsoluteFill>
      </div>
    </AbsoluteFill>
  );
};

// ── Beat 2: I-MED IMOGEN — chat types, bot hands off to a human ─────────────
const ImedBeat: React.FC = () => {
  const frame = useCurrentFrame();
  const imed = projectById("imed");
  const bubbles = [
    { text: "Hi, I need to reschedule my scan", mine: true, from: 6 },
    { text: "No problem — I can help with that. Which clinic?", mine: false, from: 12 },
    { text: "Actually it's complicated…", mine: true, from: 19 },
    { text: "Connecting you to a Genesys agent →", mine: false, from: 24, handoff: true },
  ];
  return (
    <AbsoluteFill style={{ alignItems: "center", justifyContent: "center" }}>
      <SceneTint color={imed.color} enterFrame={0} />
      <BeatTitle
        title="I-MED — IMOGEN Chatbot"
        subtitle="middleware · AI chat · Genesys human handoff"
        color={imed.color}
      />
      <div
        style={{
          width: 820,
          marginTop: 100,
          borderRadius: 28,
          border: `3px solid ${imed.color}`,
          backgroundColor: "rgba(24,24,24,0.92)",
          padding: "44px 48px",
          display: "flex",
          flexDirection: "column",
          gap: 18,
        }}
      >
        {bubbles.map((bubble) => {
          const chars = Math.max(0, Math.floor((frame - bubble.from) * 4));
          const visible = chars > 0;
          return (
            <div
              key={bubble.text}
              style={{
                alignSelf: bubble.mine ? "flex-end" : "flex-start",
                maxWidth: 560,
                padding: "14px 22px",
                borderRadius: bubble.mine
                  ? "20px 20px 4px 20px"
                  : "20px 20px 20px 4px",
                backgroundColor: bubble.handoff
                  ? "#14B8A6"
                  : bubble.mine
                  ? "#333"
                  : "#0f2e2a",
                border: bubble.handoff ? "none" : `1px solid #444`,
                color: bubble.handoff ? "#08201d" : "white",
                fontFamily: "Montserrat, sans-serif",
                fontSize: 26,
                fontWeight: bubble.handoff ? 600 : 400,
                opacity: visible ? 1 : 0,
                minHeight: 30,
              }}
            >
              {bubble.text.slice(0, chars)}
            </div>
          );
        })}
        <div
          style={{
            display: "flex",
            flexDirection: "row",
            justifyContent: "center",
            alignItems: "center",
            gap: 20,
            marginTop: 12,
            fontFamily: "'JetBrains Mono', monospace",
            fontSize: 24,
            color: imed.color,
            opacity: interpolate(frame, [27, 32], [0, 1], {
              extrapolateLeft: "clamp",
              extrapolateRight: "clamp",
            }),
          }}
        >
          <span>🤖 bot</span>
          <span>——▶</span>
          <span>🎧 human agent</span>
          <span style={{ color: "#777" }}>· pre-delivery → go-live</span>
        </div>
      </div>
    </AbsoluteFill>
  );
};

// ── Beat 3: AP+ — redacted frontend, seminar fan-out ────────────────────────
const ApplusBeat: React.FC = () => {
  const frame = useCurrentFrame();
  const applus = projectById("applus");
  const seminarChips = ["code", "plan", "design", "Gen-e2"];
  return (
    <AbsoluteFill style={{ alignItems: "center", justifyContent: "center" }}>
      <SceneTint color={applus.color} enterFrame={0} intensity={0.7} />
      <BeatTitle
        title="Australian Payments Plus"
        subtitle="Palo IT · Senior Consultant"
        color={applus.color}
      />
      <div
        style={{
          display: "flex",
          flexDirection: "row",
          gap: 70,
          marginTop: 110,
          alignItems: "center",
        }}
      >
        <div
          style={{
            width: 620,
            borderRadius: 24,
            border: `3px solid ${applus.color}`,
            backgroundColor: "rgba(24,24,24,0.92)",
            padding: "40px 44px",
          }}
        >
          <div
            style={{
              fontFamily: "'JetBrains Mono', monospace",
              fontSize: 24,
              color: applus.color,
              marginBottom: 22,
            }}
          >
            frontend.tsx — ████ (NDA)
          </div>
          {[420, 500, 360, 470, 300, 440].map((w, i) => (
            <div
              key={i}
              style={{
                width: interpolate(frame, [4 + i * 2, 10 + i * 2], [0, w], {
                  extrapolateLeft: "clamp",
                  extrapolateRight: "clamp",
                }),
                height: 26,
                borderRadius: 6,
                backgroundColor: i % 3 === 0 ? "#3a3a3a" : "#2a2a2a",
                border: "1px solid #4a4a4a",
                marginBottom: 14,
              }}
            />
          ))}
          <div
            style={{
              fontFamily: "Montserrat, sans-serif",
              fontSize: 22,
              color: "#9a9a9a",
              fontStyle: "italic",
            }}
          >
            detail unavailable due to NDA — the payments still moved 😉
          </div>
        </div>
        <div style={{ textAlign: "center" }}>
          <div
            style={{
              fontFamily: "Montserrat, sans-serif",
              fontSize: 30,
              color: "white",
              marginBottom: 26,
              maxWidth: 480,
              lineHeight: 1.5,
            }}
          >
            Seminars & consulting: using AI to code, plan and design — with
            quality and a high success rate
          </div>
          <div
            style={{
              display: "flex",
              flexWrap: "wrap",
              justifyContent: "center",
            }}
          >
            {seminarChips.map((chip, i) => (
              <StackChip
                key={chip}
                text={chip}
                color={applus.color}
                enterFrame={14 + i * 4}
                fontSize={28}
              />
            ))}
          </div>
        </div>
      </div>
    </AbsoluteFill>
  );
};

// ── Beat 4: UrbnSurf — a wave sweeps in the feature checklist ───────────────
const UrbnsurfBeat: React.FC = () => {
  const frame = useCurrentFrame();
  const urbnsurf = projectById("urbnsurf");
  const waveX = interpolate(frame, [0, 44], [-2100, 300], {
    extrapolateLeft: "clamp",
    extrapolateRight: "clamp",
  });
  return (
    <AbsoluteFill style={{ alignItems: "center", justifyContent: "center" }}>
      <SceneTint color={urbnsurf.color} enterFrame={0} />
      <BeatTitle
        title="UrbnSurf — Phase 1 + Phase 1.5"
        subtitle="Palo IT · Senior Consultant · shortly after discovery"
        color={urbnsurf.color}
      />
      <div
        style={{
          display: "grid",
          gridTemplateColumns: "repeat(2, 640px)",
          gap: "26px 60px",
          marginTop: 120,
        }}
      >
        {urbnsurf.bullets.map((bullet, i) => (
          <div
            key={bullet}
            style={{
              display: "flex",
              flexDirection: "row",
              alignItems: "center",
              gap: 18,
              fontFamily: "Montserrat, sans-serif",
              fontSize: 30,
              color: "white",
              opacity: interpolate(
                frame,
                [8 + i * 6, 14 + i * 6],
                [0, 1],
                { extrapolateLeft: "clamp", extrapolateRight: "clamp" }
              ),
              translate: `${interpolate(
                frame,
                [8 + i * 6, 14 + i * 6],
                [-60, 0],
                { extrapolateLeft: "clamp", extrapolateRight: "clamp" }
              )}px 0px`,
            }}
          >
            <span
              style={{
                color: urbnsurf.color,
                fontSize: 34,
                fontWeight: 600,
              }}
            >
              ✓
            </span>
            {bullet}
          </div>
        ))}
      </div>
      <svg
        width={4000}
        height={300}
        viewBox="0 0 4000 300"
        style={{
          position: "absolute",
          bottom: 60,
          left: 0,
          translate: `${waveX}px 0px`,
          opacity: 0.5,
        }}
      >
        <path
          d="M 0 150 Q 250 30, 500 150 T 1000 150 T 1500 150 T 2000 150 T 2500 150 T 3000 150 T 3500 150 T 4000 150 L 4000 300 L 0 300 Z"
          fill={urbnsurf.color}
        />
      </svg>
    </AbsoluteFill>
  );
};

// ── Beat 5: Virgin Active — old site swipes away, pins drop ─────────────────
const VirginBeat: React.FC = () => {
  const frame = useCurrentFrame();
  const { fps } = useVideoConfig();
  const virgin = projectById("virginactive");
  const newSite = spring({
    frame: frame - 8,
    fps,
    config: { damping: 14, stiffness: 130 },
  });
  return (
    <AbsoluteFill style={{ alignItems: "center", justifyContent: "center" }}>
      <SceneTint color={virgin.color} enterFrame={0} />
      <BeatTitle
        title="Virgin Active"
        subtitle="new website & features · SG · TH · AU"
        color={virgin.color}
      />
      <div style={{ position: "relative", width: 1000, height: 600, marginTop: 90 }}>
        {/* Old site swipes away */}
        <div
          style={{
            position: "absolute",
            inset: 0,
            borderRadius: 20,
            border: "3px solid #555",
            backgroundColor: "#2a2a2a",
            padding: 30,
            translate: `${interpolate(frame, [6, 20], [0, -1400], {
              extrapolateLeft: "clamp",
              extrapolateRight: "clamp",
            })}px 0px`,
            rotate: `${interpolate(frame, [6, 20], [0, -14], {
              extrapolateLeft: "clamp",
              extrapolateRight: "clamp",
            })}deg`,
          }}
        >
          <div style={{ display: "flex", gap: 10, marginBottom: 24 }}>
            {["#555", "#555", "#555"].map((c, i) => (
              <div key={i} style={{ width: 14, height: 14, borderRadius: 7, backgroundColor: c }} />
            ))}
          </div>
          {[700, 500, 620, 420].map((w, i) => (
            <div
              key={i}
              style={{
                width: w,
                height: 34,
                backgroundColor: "#3d3d3d",
                marginBottom: 18,
                borderRadius: 4,
              }}
            />
          ))}
          <div
            style={{
              fontFamily: "'JetBrains Mono', monospace",
              color: "#777",
              fontSize: 26,
              marginTop: 30,
            }}
          >
            legacy site
          </div>
        </div>
        {/* New site snaps in */}
        <div
          style={{
            position: "absolute",
            inset: 0,
            borderRadius: 20,
            border: `3px solid ${virgin.color}`,
            background: "linear-gradient(160deg, #241214 0%, #181818 60%)",
            padding: 30,
            scale: String(0.7 + newSite * 0.3),
            opacity: newSite,
            boxShadow: `0 0 80px ${virgin.color}44`,
          }}
        >
          <div style={{ display: "flex", gap: 10, marginBottom: 24 }}>
            {["#ff5f57", "#febc2e", "#28c840"].map((c) => (
              <div key={c} style={{ width: 14, height: 14, borderRadius: 7, backgroundColor: c }} />
            ))}
          </div>
          <div
            style={{
              fontFamily: "Montserrat, sans-serif",
              fontWeight: 700,
              fontSize: 54,
              color: "white",
            }}
          >
            Virgin Active
          </div>
          <div
            style={{
              fontFamily: "Montserrat, sans-serif",
              fontSize: 28,
              color: "#d6d6d6",
              marginTop: 12,
            }}
          >
            New website · new features · MyLocker for gym goers
          </div>
          <div
            style={{
              display: "flex",
              flexDirection: "row",
              gap: 50,
              marginTop: 70,
              justifyContent: "center",
            }}
          >
            {["SG", "TH", "AU"].map((pin, i) => (
              <div
                key={pin}
                style={{
                  textAlign: "center",
                  translate: `0px ${
                    (1 -
                      spring({
                        frame: frame - (18 + i * 4),
                        fps,
                        config: { damping: 11, stiffness: 200 },
                      })) *
                    -260
                  }px`,
                }}
              >
                <div style={{ fontSize: 60 }}>📍</div>
                <div
                  style={{
                    fontFamily: "'JetBrains Mono', monospace",
                    fontSize: 28,
                    color: virgin.color,
                    fontWeight: 600,
                  }}
                >
                  {pin}
                </div>
              </div>
            ))}
          </div>
        </div>
      </div>
    </AbsoluteFill>
  );
};

// ── Outro: five engagement colors streak into the timeline HUD ──────────────
const OutroBeat: React.FC = () => {
  const frame = useCurrentFrame();
  const colors = ["#8B5CF6", "#14B8A6", "#FFD100", "#0EA5E9", "#E10A0A"];
  return (
    <AbsoluteFill>
      {colors.map((color, i) => (
        <div
          key={color}
          style={{
            position: "absolute",
            left: 660 + i * 150,
            top: 0,
            width: 10,
            height: 340,
            borderRadius: 5,
            background: `linear-gradient(180deg, transparent, ${color})`,
            translate: `0px ${interpolate(
              frame,
              [i * 1.2, 7 + i * 1.2],
              [-360, 1080],
              { extrapolateLeft: "clamp", extrapolateRight: "clamp" }
            )}px`,
          }}
        />
      ))}
    </AbsoluteFill>
  );
};

// Scene 5 — Senior Consultant Era (60–88%): chapter intro, five engagement
// beats, and an outro streaking the era colors into the HUD.
const Scene5SeniorEra: React.FC = () => {
  return (
    <AbsoluteFill style={{ backgroundColor: "#181818" }}>
      <Sequence
        from={beatLocal(SENIOR_BEATS.intro).from}
        durationInFrames={SENIOR_BEATS.intro.duration + 4}
        layout="absolute-fill"
        name="Intro"
      >
        <BeatFade duration={SENIOR_BEATS.intro.duration + 4}>
          <IntroBeat />
        </BeatFade>
      </Sequence>
      <Sequence
        from={beatLocal(SENIOR_BEATS.vista).from}
        durationInFrames={SENIOR_BEATS.vista.duration + 4}
        layout="absolute-fill"
        name="Vista"
      >
        <BeatFade duration={SENIOR_BEATS.vista.duration + 4}>
          <VistaBeat />
        </BeatFade>
      </Sequence>
      <Sequence
        from={beatLocal(SENIOR_BEATS.imed).from}
        durationInFrames={SENIOR_BEATS.imed.duration + 4}
        layout="absolute-fill"
        name="I-MED"
      >
        <BeatFade duration={SENIOR_BEATS.imed.duration + 4}>
          <ImedBeat />
        </BeatFade>
      </Sequence>
      <Sequence
        from={beatLocal(SENIOR_BEATS.applus).from}
        durationInFrames={SENIOR_BEATS.applus.duration + 4}
        layout="absolute-fill"
        name="AP+"
      >
        <BeatFade duration={SENIOR_BEATS.applus.duration + 4}>
          <ApplusBeat />
        </BeatFade>
      </Sequence>
      <Sequence
        from={beatLocal(SENIOR_BEATS.urbnsurf).from}
        durationInFrames={SENIOR_BEATS.urbnsurf.duration + 4}
        layout="absolute-fill"
        name="UrbnSurf"
      >
        <BeatFade duration={SENIOR_BEATS.urbnsurf.duration + 4}>
          <UrbnsurfBeat />
        </BeatFade>
      </Sequence>
      <Sequence
        from={beatLocal(SENIOR_BEATS.virginactive).from}
        durationInFrames={SENIOR_BEATS.virginactive.duration + 4}
        layout="absolute-fill"
        name="Virgin Active"
      >
        <BeatFade duration={SENIOR_BEATS.virginactive.duration + 4}>
          <VirginBeat />
        </BeatFade>
      </Sequence>
      <Sequence
        from={beatLocal(SENIOR_BEATS.outro).from}
        durationInFrames={SENIOR_BEATS.outro.duration + 4}
        layout="absolute-fill"
        name="Outro"
      >
        <OutroBeat />
      </Sequence>
    </AbsoluteFill>
  );
};

export default Scene5SeniorEra;
